import { Copy, File as FileIcon, Search, Trash2, Upload } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { deleteFile, listFiles, uploadFile, type FileRecord } from "@/services/files";

export function FileManager() {
  const [files, setFiles] = React.useState<FileRecord[]>([]);
  const [folder, setFolder] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [message, setMessage] = React.useState("");

  const loadFiles = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFiles(folder);
      setFiles(data);
    } catch (error) {
      setMessage(`加载失败：${String(error)}`);
    } finally {
      setLoading(false);
    }
  }, [folder]);

  React.useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage("");
    try {
      await uploadFile(file, folder);
      setMessage(`上传成功：${file.name}`);
      await loadFiles();
    } catch (error) {
      setMessage(`上传失败：${String(error)}`);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await deleteFile(key);
      setMessage("删除成功");
      await loadFiles();
    } catch (error) {
      setMessage(`删除失败：${String(error)}`);
    }
  };

  const handleCopy = (url: string) => {
    void navigator.clipboard.writeText(url);
    setMessage("链接已复制");
  };

  const filtered = files.filter((f) => f.filename.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="grid h-full gap-4 lg:grid-cols-[280px_1fr]">
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-4 w-4" />
            上传文件
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="文件夹前缀，例如 images"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            className="text-xs"
          />
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed p-6 text-sm text-muted-foreground hover:bg-accent">
            <Upload className="h-6 w-6" />
            <span>{uploading ? "上传中..." : "点击或拖拽上传文件"}</span>
            <input type="file" className="hidden" onChange={handleFileChange} disabled={uploading} />
          </label>
          {message && <p className="text-xs text-muted-foreground">{message}</p>}
        </CardContent>
      </Card>

      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <FileIcon className="h-4 w-4" />
              文件列表
            </span>
            <Button size="sm" variant="outline" onClick={() => void loadFiles()} disabled={loading}>
              {loading ? "加载中..." : "刷新"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜索文件..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
          <div className="flex-1 overflow-auto rounded-md border">
            {filtered.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">暂无文件</div>
            )}
            <div className="divide-y">
              {filtered.map((file) => (
                <div
                  key={file.key}
                  className="flex items-center justify-between gap-2 p-3 text-sm hover:bg-accent"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{file.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB · {new Date(file.last_modified).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleCopy(file.url)}
                      title="复制链接"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => void handleDelete(file.key)}
                      title="删除"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
